// v2/mention-input.tsx — Input com autocomplete de @menção (FP4). Envolve textarea
// OU input de 1 linha. Contrato: value/onChange trafegam o texto ARMAZENÁVEL.
// USER = @username plano (já limpo). LIVRO/CLUBE = token @[Label](tipo:id) no
// armazenado, MAS o campo MOSTRA "@Label" limpo (transformação display↔token na
// borda; as transformações são inversas, então o cursor não pula). `plain` desliga
// a transformação (composer2 do clube usa, p/ o MdToolbar operar sobre o token cru).
import React, { useRef, useState, useMemo } from 'react';
import { MentionType, mentionToken, useMentionData } from './mentions';
import { semAcento } from './note-card';

export interface MPerson { id: string; name?: string; username?: string; avatarUrl?: string | null }
export interface MBook { id: string; title?: string; coverUrl?: string | null }
export interface MClub { id: string; name?: string }

interface Sugg { type: MentionType; id: string; label: string; handle?: string; sub?: string; dot: string }

// ── Transformação token (armazenado) ↔ display (limpo) p/ LIVRO/CLUBE ──────────
const BOOKCLUB_RE = /@\[([^\]]+)\]\((book|club):([a-f0-9-]{36})\)/g;
const escapeRegex = (s: string) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
// token → "@Label" (user @username e texto comum ficam intactos).
function canonicalToDisplay(value: string): string {
  return String(value || '').replace(new RegExp(BOOKCLUB_RE.source, 'g'), (_m, label) => '@' + label);
}
function parseBookClub(value: string): { label: string; type: MentionType; id: string }[] {
  const out: { label: string; type: MentionType; id: string }[] = [];
  const re = new RegExp(BOOKCLUB_RE.source, 'g');
  let m: RegExpExecArray | null;
  while ((m = re.exec(String(value || '')))) out.push({ label: m[1], type: m[2] as MentionType, id: m[3] });
  return out;
}
// "@Label" → token (só p/ labels do registro, maior primeiro; fronteira evita
// casar prefixo de palavra ou e-mail; ponto/pontuação terminam a menção).
function displayToCanonical(display: string, registry: { label: string; type: MentionType; id: string }[]): string {
  let out = String(display || '');
  const sorted = registry.slice().sort((a, b) => (b.label || '').length - (a.label || '').length);
  for (const r of sorted) {
    if (!r.label) continue;
    const re = new RegExp('(^|[^\\p{L}\\p{N}_@])@' + escapeRegex(r.label) + '(?![\\p{L}\\p{N}_])', 'gu');
    out = out.replace(re, (_m, b) => b + mentionToken(r.type, r.id, r.label));
  }
  return out;
}

export const MentionInput: React.FC<{
  value: string;
  onChange: (v: string) => void;
  multiline?: boolean;
  placeholder?: string;
  maxLength?: number;
  autoFocus?: boolean;
  rows?: number;
  disabled?: boolean;
  inputStyle?: React.CSSProperties;
  wrapStyle?: React.CSSProperties;
  people?: MPerson[];
  books?: MBook[];
  clubs?: MClub[];
  dropdownPosition?: 'above' | 'below';
  onEnter?: () => void; // inputs de 1 linha: Enter envia (só quando o dropdown está fechado)
  onSubmit?: () => void; // Cmd/Ctrl+Enter envia (textarea multiline)
  inputRef?: React.MutableRefObject<any>; // ref externo opcional (preserva auto-focus de quem usava ref)
  plain?: boolean; // true → mostra o valor cru (token), sem transformação (composer2 + MdToolbar)
}> = ({ value, onChange, multiline, placeholder, maxLength, autoFocus, rows = 3, disabled, inputStyle, wrapStyle, people: peopleProp, books: booksProp, clubs: clubsProp, dropdownPosition = 'above', onEnter, onSubmit, inputRef, plain }) => {
  const ctx = useMentionData();
  const people = peopleProp ?? ctx.people ?? [];
  const books = booksProp ?? ctx.books ?? [];
  const clubs = clubsProp ?? ctx.clubs ?? [];
  const ref = useRef<any>(null);
  const [query, setQuery] = useState<{ at: number; q: string } | null>(null);
  const [active, setActive] = useState(0);
  // SEM ACENTO DOS DOIS LADOS. So baixar a caixa fazia `@andre` nao achar
  // "Andre" com acento, `@habitos` nao achar "Habitos Atomicos" e `@decadas`
  // nao achar "Decadas de Leitura" — e a pessoa concluia que nao dava para
  // marcar aquilo. O app ja tinha `semAcento` resolvendo isto para notas; a
  // correcao nunca chegou nas mencoes.
  const norm = (s?: string) => semAcento(s);

  // o que o campo mostra: cru (plain) ou display limpo (token→@Label)
  const shown = plain ? (value || '') : canonicalToDisplay(value || '');
  // registro p/ reconverter display→token = os tokens JÁ presentes no valor (fonte
  // da verdade). Sem cache persistente: a escolha atual entra como temp no pick().
  const buildRegistry = (): { label: string; type: MentionType; id: string }[] => parseBookClub(value || '');
  const toStored = (sh: string) => (plain ? sh : displayToCanonical(sh, buildRegistry()));

  const detect = (el: any) => {
    const sh = (el && el.value) != null ? el.value : shown;
    const pos = el?.selectionStart ?? sh.length;
    const before = sh.slice(0, pos);
    const m = before.match(/(?:^|\s)@([^\s@[\]()]{0,30})$/);
    if (m) { setQuery({ at: pos - m[1].length - 1, q: m[1] }); setActive(0); }
    else setQuery(null);
  };

  const suggestions: Sugg[] = useMemo(() => {
    if (!query) return [];
    const q = norm(query.q);
    const ppl = (people || []).filter(p => norm(p.name).includes(q) || norm(p.username).includes(q)).slice(0, 6)
      .map(p => ({ type: 'user' as MentionType, id: p.id, label: p.name || p.username || 'usuário', handle: p.username, sub: p.username ? '@' + p.username : 'pessoa', dot: 'var(--ember-2, var(--amber, #e07a3b))' }));
    const bks = (books || []).filter(b => norm(b.title).includes(q)).slice(0, 5)
      .map(b => ({ type: 'book' as MentionType, id: b.id, label: b.title || 'livro', sub: 'livro', dot: '#6FA8A0' }));
    const cls = (clubs || []).filter(c => norm(c.name).includes(q)).slice(0, 5)
      .map(c => ({ type: 'club' as MentionType, id: c.id, label: c.name || 'clube', sub: 'clube', dot: '#9E9485' }));
    return [...ppl, ...bks, ...cls];
  }, [query, people, books, clubs]);

  const open = !disabled && !!query && suggestions.length > 0;

  const pick = (s: Sugg) => {
    if (!query) return;
    // forma inserida no DISPLAY: user→@username; livro/clube→@Label limpo (ou token cru se plain)
    const insertForm = (s.type === 'user' && s.handle) ? '@' + s.handle
      : (plain ? mentionToken(s.type, s.id, s.label) : '@' + s.label);
    const cur = (ref.current && ref.current.value != null) ? ref.current.value : shown;
    const caretNow = ref.current?.selectionStart ?? cur.length;
    const nextShown = cur.slice(0, query.at) + insertForm + ' ' + cur.slice(caretNow);
    // registro temporário só desta chamada: tokens do valor + a escolha atual (que
    // ainda não está no valor). Nada persiste → não re-tokeniza menção apagada.
    const reg = buildRegistry();
    if (!plain && s.type !== 'user' && s.id) reg.push({ label: s.label, type: s.type, id: s.id });
    const nextStored = plain ? nextShown : displayToCanonical(nextShown, reg);
    if (maxLength && nextStored.length > maxLength) { setQuery(null); return; } // não estoura o limite armazenado
    onChange(nextStored);
    setQuery(null); setActive(0);
    const caret = query.at + insertForm.length + 1; // posição no DISPLAY (= o que o campo mostra)
    requestAnimationFrame(() => { try { ref.current?.focus(); ref.current?.setSelectionRange(caret, caret); } catch { /* noop */ } });
  };

  const onKeyDown = (e: React.KeyboardEvent<any>) => {
    if (open) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(suggestions.length - 1, a + 1)); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(0, a - 1)); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pick(suggestions[active] || suggestions[0]); return; }
      if (e.key === 'Escape') { e.preventDefault(); setQuery(null); return; }
    } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && onSubmit) { e.preventDefault(); onSubmit(); }
    else if (e.key === 'Enter' && !multiline && onEnter) { e.preventDefault(); onEnter(); }
  };

  const common: any = {
    ref: (el: any) => { ref.current = el; if (inputRef) inputRef.current = el; },
    // maxLength NÃO vai pro DOM: limitaria o DISPLAY (mais curto), mas o limite real
    // é sobre o ARMAZENADO (token de livro/clube ocupa mais). Imposto no onChange.
    value: shown, disabled, placeholder, autoFocus, style: inputStyle,
    onChange: (e: any) => {
      const stored = toStored(e.target.value);
      if (maxLength && stored.length > maxLength) { if (ref.current) ref.current.value = shown; return; } // reverte (sem drift do controlled)
      onChange(stored); detect(e.target);
    },
    onKeyUp: (e: any) => detect(e.target),
    onClick: (e: any) => detect(e.target),
    onKeyDown,
    onBlur: () => setTimeout(() => setQuery(null), 120), // deixa o onMouseDown do item disparar
  };

  return (
    <div style={{ position: 'relative', ...wrapStyle }}>
      {multiline ? <textarea {...common} rows={rows} /> : <input {...common} />}
      {open && (
        <div style={{
          position: 'absolute', left: 0, right: 0, zIndex: 70,
          ...(dropdownPosition === 'above' ? { bottom: '100%', marginBottom: 6 } : { top: '100%', marginTop: 6 }),
          background: 'var(--paper, var(--card, #1a1410))', border: '1px solid var(--line, var(--hair, rgba(255,255,255,0.1)))', borderRadius: 14,
          boxShadow: '0 12px 30px rgba(0,0,0,0.35)', overflow: 'hidden', maxHeight: 248, overflowY: 'auto',
        }}>
          {suggestions.map((s, i) => (
            <button key={s.type + s.id} type="button" onMouseDown={(e) => { e.preventDefault(); pick(s); }} style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 13px',
              background: i === active ? 'var(--paper-2, var(--card-2, rgba(255,255,255,0.06)))' : 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
            }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.dot, flexShrink: 0 }} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 14, color: 'var(--ink, var(--cream, #f5efe6))', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.label}</span>
                <span style={{ display: 'block', fontSize: 11, color: 'var(--ink-3, var(--muted, #9a8f80))' }}>{s.sub}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
